import { useState, useEffect, useMemo } from "react";
import CompanyService, {
  DataType,
  Entities
} from "../../../services/CompanyService";
import Dropdown from "../../shared/components/Dropdown";
import {
  EntityField,
  sortEntityFields
} from "../../shared/entities/EntityField";
import { TranslationService } from "../../../services/TranslationService";
import { InvoiceEditStatus } from "./entities/InvoiceEditStatus";
import FieldEdit, { ifOldFormat } from "./InvoiceMassiveChangesFieldEdit";
import { InvoiceGetResponse } from "./entities/InvoiceGetResponse";
import { Cast } from "../../../utils/Utils";
import { RequiredManager } from "../../shared/RequieredManager";
import { getClientDefaultFieldProperty } from "../../../services/ClientService";
import { formatIntizaDate } from "../../../utils/FormatUtils";
import { DefaultValueEnum } from "../../../entities/company/IOStatus/IOStatusEntity";

export const InvoiceChangeStatusForm = ({
  onSubmit,
  personId,
  defaultStatusId,
  defaultAdditionals
}: {
  onSubmit: (statusEdit: InvoiceEditStatus) => Promise<void>;
  personId: string;
  defaultStatusId?: number;
  defaultAdditionals?: {
    Id: string;
    Value: string;
  }[];
}) => {
  const invoice = defaultAdditionals
    ? Cast<InvoiceGetResponse.Item>({ additionals: defaultAdditionals })
    : undefined;
  const [status, setStatus] = useState(
    new InvoiceEditStatus(CompanyService.getUserid(), defaultStatusId, invoice)
  );
  const [isSaving, setIsSaving] = useState(false);
  const { translate } = TranslationService;
  const statusList = CompanyService.getAllowedInvoiceStatuses();

  const fields = useMemo(() => {
    if (!status.statusId) return [];
    return CompanyService.getAdditionalDefinitionsFiltered(Entities.Invoice)
      .filter(
        (x) =>
          x.IOStatusDependencies.filter((y) => y.IOStatusID === status.statusId)
            .length > 0
      )
      .map((x) => new EntityField(x))
      .sort(sortEntityFields(CompanyService.getIoSortedFields().split(",")));
  }, [status.statusId]);

  const getFieldDependency = (fieldId: number): any => {
    return CompanyService.getAdditionalDefinitionsFiltered(Entities.Invoice)
      .find((x) => x.AdditionalDefinitionID === fieldId)
      ?.IOStatusDependencies.find((y: any) => y.IOStatusID === status.statusId);
  };

  const getDefaultValue = (field: EntityField): string | undefined => {
    const dependency = getFieldDependency(field.id);
    if (!dependency || !(dependency as any).DefaultValue) {
      return undefined;
    }

    const defaultValue = (dependency as any).DefaultValue;
    if (field.type === DataType.Date) {
      return formatIntizaDate(new Date());
    } else if (
      field.type === DataType.Text &&
      defaultValue === DefaultValueEnum.NombreYApellido
    ) {
      return (
        CompanyService.getUsers()?.find(
          (x) => x.Id === CompanyService.getUserid()
        )?.Value ?? ""
      );
    }
    return undefined;
  };

  const isFieldEditable = (fieldId: number): boolean => {
    const dependency = getFieldDependency(fieldId);

    if (!dependency) return true; // Por defecto editable si no hay dependencia

    const isEditable = (dependency as any)?.IsEditable;
    // Si es explícitamente false, es readonly
    if (isEditable === false) return false;
    // Si es null o undefined, por defecto es editable (true)
    return true;
  };

  const requiredFields = fields.map((field) => {
    const dependency = getFieldDependency(field.id);
    const mandatory = dependency?.Mandatory;
    // Asegurar que solo campos con Mandatory === true sean obligatorios
    const isRequired = mandatory === true || mandatory === "true";
    return {
      id: field.id,
      required: isRequired
    };
  });

  const requiredFieldsIds = requiredFields
    .filter((fx) => fx.required)
    .map((f) => f.id);
  const requiredManager = new RequiredManager();

  // Aplicar valores por defecto cuando cambia el estado
  useEffect(() => {
    if (!status.statusId) return;

    const defaultValues = fields
      .filter((field) => {
        const dependency = getFieldDependency(field.id);
        if (!(dependency as any)?.DefaultValue) return false;

        const existingValue = status.additionals.find((a) => a.Id === field.id);
        // Agregar si no existe o si existe pero está vacío
        return (
          !existingValue || !existingValue.Value || existingValue.Value === ""
        );
      })
      .map((field) => {
        const defaultValue = getDefaultValue(field);
        return { Id: field.id, Value: defaultValue ?? "" };
      });

    if (defaultValues.length > 0) {
      setStatus((prev) => ({
        ...prev,
        additionals: [
          // Mantener los campos que no tienen valor por defecto o que ya tienen un valor válido
          ...prev.additionals.filter((a) => {
            const hasDefaultValue = defaultValues.find((d) => d.Id === a.Id);
            // Si tiene valor por defecto y está vacío, lo reemplazaremos
            if (hasDefaultValue && (!a.Value || a.Value === "")) {
              return false;
            }
            return true;
          }),
          // Agregar o actualizar con los valores por defecto
          ...defaultValues
        ]
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.statusId]);

  const onFieldValueChange =
    (field: EntityField) => (value: string | undefined) => {
      if (!status) {
        return;
      }
      if (value && field?.type === DataType.Date) {
        value = ifOldFormat(value);
      }

      if (field.isDefault()) {
        const clientProperty = getClientDefaultFieldProperty(field.id);
        setStatus({ ...status, [clientProperty]: value });
      } else {
        const newAdditionals = [
          ...status.additionals.filter((x) => x.Id !== field.id),
          { Id: field.id, Value: value ?? "" }
        ];
        setStatus({ ...status, additionals: newAdditionals });
      }
    };

  const statusChange = (id?: number) => {
    setStatus(new InvoiceEditStatus(status.userId, id));
  };

  const saveStatus = async () => {
    if (isSaving || !requiredManager.validate()) {
      return;
    }
    setIsSaving(true);

    // Asegurar que todos los campos con valor por defecto estén en additionals con su valor correcto
    // Especialmente importante para campos no editables que son obligatorios
    const defaultValuesToAdd = fields
      .filter((field) => {
        const dependency = getFieldDependency(field.id);
        return (dependency as any)?.DefaultValue;
      })
      .map((field) => {
        const defaultValue = getDefaultValue(field);
        const existingValue = status.additionals.find((a) => a.Id === field.id);
        // Si no existe en additionals o existe pero está vacío, usar el valor por defecto
        const finalValue =
          !existingValue || !existingValue.Value || existingValue.Value === ""
            ? (defaultValue ?? "")
            : existingValue.Value;
        return { Id: field.id, Value: finalValue };
      });

    // Crear el estado final con todos los valores por defecto incluidos
    // Reemplazar los valores existentes si están vacíos o no existen
    const finalStatus = {
      ...status,
      additionals: [
        // Mantener los campos que no tienen valor por defecto o que ya tienen un valor válido
        ...status.additionals.filter((a) => {
          const hasDefaultValue = defaultValuesToAdd.find((d) => d.Id === a.Id);
          // Si tiene valor por defecto, lo reemplazaremos con el valor por defecto
          // Si no tiene valor por defecto, mantenerlo
          return !hasDefaultValue;
        }),
        // Agregar o actualizar con los valores por defecto
        ...defaultValuesToAdd
      ]
    };

    await onSubmit(finalStatus);
    setIsSaving(false);
  };

  return (
    <div className="d-flex flex-column justify-content-between h-100 p-4">
      <div style={{ minHeight: 300 }}>
        <div className="d-flex input-column mb-3">
          <label className="form-label">{translate.NewStatus}</label>
          <Dropdown
            optionLabel={translate.Select}
            onChange={statusChange}
            items={statusList.map((x) => ({
              value: x.IOStatusID,
              text: x.Value
            }))}
            defaultValue={status.statusId}
          />
        </div>

        {fields.map((field) => {
          const editable = isFieldEditable(field.id);
          const defaultValue = getDefaultValue(field);
          const currentValue = status.additionals.find(
            (a) => a.Id === field.id
          )?.Value;

          return (
            <FieldEdit
              key={field.id}
              field={field}
              onChange={onFieldValueChange(field)}
              status={status}
              personId={personId}
              invoice={invoice}
              requiredManager={requiredManager}
              fieldsToValidate={requiredFieldsIds}
              readOnly={!editable}
              defaultValue={editable ? undefined : currentValue || defaultValue}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          className="btn btn-primary"
          onClick={saveStatus}
          disabled={status.statusId === undefined}
        >
          {translate.Save}
          {isSaving && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </div>
    </div>
  );
};
